import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Mail, Lock, ArrowLeft, Send, CheckCircle, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const resetToken = searchParams.get('token');
  
  const [step, setStep] = useState(resetToken ? 'reset' : 'request'); // 'request', 'sent', 'reset', 'success'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const lang = i18n.language;

  const content = {
    en: {
      request: {
        title: "Forgot Password?",
        subtitle: "Enter your email and we'll send you a reset link",
        email: "Email Address",
        submit: "Send Reset Link",
        back: "Back to Login"
      },
      sent: {
        title: "Check Your Email",
        subtitle: "We've sent a password reset link to",
        note: "The link is valid for 1 hour. Check your spam folder if you don't see it.",
        resend: "Didn't receive it? Resend",
        back: "Back to Login"
      },
      reset: {
        title: "Create New Password",
        subtitle: "Enter your new password below",
        password: "New Password",
        confirmPassword: "Confirm Password",
        submit: "Reset Password",
        requirements: "Minimum 6 characters"
      },
      success: {
        title: "Password Reset!",
        subtitle: "Your password has been successfully changed",
        login: "Go to Login"
      },
      errors: {
        emailRequired: "Please enter your email address",
        passwordRequired: "Please enter a new password",
        passwordMin: "Password must be at least 6 characters",
        passwordMatch: "Passwords do not match",
        failed: "Something went wrong. Please try again."
      }
    },
    nl: {
      request: {
        title: "Wachtwoord Vergeten?",
        subtitle: "Voer je e-mail in en we sturen je een reset link",
        email: "E-mailadres",
        submit: "Stuur Reset Link",
        back: "Terug naar Login"
      },
      sent: {
        title: "Controleer Je E-mail",
        subtitle: "We hebben een wachtwoord reset link gestuurd naar",
        note: "De link is 1 uur geldig. Controleer je spam folder als je hem niet ziet.",
        resend: "Niet ontvangen? Opnieuw versturen",
        back: "Terug naar Login"
      },
      reset: {
        title: "Nieuw Wachtwoord Aanmaken",
        subtitle: "Voer hieronder je nieuwe wachtwoord in",
        password: "Nieuw Wachtwoord",
        confirmPassword: "Bevestig Wachtwoord",
        submit: "Wachtwoord Resetten",
        requirements: "Minimaal 6 tekens"
      },
      success: {
        title: "Wachtwoord Gereset!",
        subtitle: "Je wachtwoord is succesvol gewijzigd",
        login: "Ga naar Login"
      },
      errors: {
        emailRequired: "Voer je e-mailadres in",
        passwordRequired: "Voer een nieuw wachtwoord in",
        passwordMin: "Wachtwoord moet minimaal 6 tekens zijn",
        passwordMatch: "Wachtwoorden komen niet overeen",
        failed: "Er ging iets mis. Probeer het opnieuw."
      }
    },
    de: {
      request: { title: "Passwort vergessen?", subtitle: "Gib deine E-Mail ein und wir senden dir einen Reset-Link", email: "E-Mail-Adresse", submit: "Reset-Link senden", back: "Zurück zum Login" },
      sent: { title: "E-Mail prüfen", subtitle: "Wir haben einen Passwort-Reset-Link gesendet an", note: "Der Link ist 1 Stunde gültig.", resend: "Nicht erhalten? Erneut senden", back: "Zurück zum Login" },
      reset: { title: "Neues Passwort erstellen", subtitle: "Gib unten dein neues Passwort ein", password: "Neues Passwort", confirmPassword: "Passwort bestätigen", submit: "Passwort zurücksetzen", requirements: "Mindestens 6 Zeichen" },
      success: { title: "Passwort zurückgesetzt!", subtitle: "Dein Passwort wurde erfolgreich geändert", login: "Zum Login" },
      errors: { emailRequired: "Bitte E-Mail eingeben", passwordRequired: "Bitte neues Passwort eingeben", passwordMin: "Mindestens 6 Zeichen", passwordMatch: "Passwörter stimmen nicht überein", failed: "Etwas ist schief gelaufen." }
    },
    fr: {
      request: { title: "Mot de passe oublié?", subtitle: "Entrez votre email et nous vous enverrons un lien de réinitialisation", email: "Adresse email", submit: "Envoyer le lien", back: "Retour à la connexion" },
      sent: { title: "Vérifiez votre email", subtitle: "Nous avons envoyé un lien de réinitialisation à", note: "Le lien est valide pendant 1 heure.", resend: "Pas reçu? Renvoyer", back: "Retour à la connexion" },
      reset: { title: "Créer un nouveau mot de passe", subtitle: "Entrez votre nouveau mot de passe ci-dessous", password: "Nouveau mot de passe", confirmPassword: "Confirmer", submit: "Réinitialiser", requirements: "Minimum 6 caractères" },
      success: { title: "Mot de passe réinitialisé!", subtitle: "Votre mot de passe a été changé avec succès", login: "Aller à la connexion" },
      errors: { emailRequired: "Veuillez entrer votre email", passwordRequired: "Veuillez entrer un mot de passe", passwordMin: "6 caractères minimum", passwordMatch: "Les mots de passe ne correspondent pas", failed: "Quelque chose s'est mal passé." }
    }
  };

  const t = content[lang] || content.en;

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error(t.errors.emailRequired);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setStep('sent');
      toast.success('Reset link sent!');
    } catch (error) {
      toast.error(t.errors.failed);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error(t.errors.passwordRequired);
      return;
    }
    if (password.length < 6) {
      toast.error(t.errors.passwordMin);
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t.errors.passwordMatch);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: password
      });
      setStep('success');
      toast.success('Password reset successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || t.errors.failed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="forgot-password-page">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 flex items-center justify-center min-h-[80vh]">
        <div className="w-full max-w-md">
          
          {/* Request Reset Form */}
          {step === 'request' && (
            <div className="p-8 bg-white/5 rounded-2xl border border-white/10">
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <KeyRound className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h1 className="text-2xl font-bold mb-2">{t.request.title}</h1>
                <p className="text-gray-400">{t.request.subtitle}</p>
              </div>

              <form onSubmit={handleRequestReset} className="space-y-6">
                <div>
                  <Label>{t.request.email}</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10 bg-white/5 border-white/10"
                      data-testid="reset-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                  data-testid="send-reset-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? '...' : t.request.submit}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/login" className="text-sm text-gray-400 hover:text-[#D4AF37] flex items-center justify-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  {t.request.back}
                </Link>
              </div>
            </div>
          )}

          {/* Email Sent Confirmation */}
          {step === 'sent' && (
            <div className="p-8 bg-white/5 rounded-2xl border border-white/10 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <Mail className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold mb-2">{t.sent.title}</h1>
              <p className="text-gray-400 mb-2">{t.sent.subtitle}</p>
              <p className="text-[#D4AF37] font-medium mb-4">{email}</p>
              <p className="text-sm text-gray-500 mb-6">{t.sent.note}</p>
              
              <Button
                onClick={() => setStep('request')}
                variant="outline"
                className="border-white/10 mb-4"
              >
                {t.sent.resend}
              </Button>
              
              <div>
                <Link to="/login" className="text-sm text-gray-400 hover:text-[#D4AF37]">
                  {t.sent.back}
                </Link>
              </div>
            </div>
          )}

          {/* Reset Password Form */}
          {step === 'reset' && (
            <div className="p-8 bg-white/5 rounded-2xl border border-white/10">
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <Lock className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h1 className="text-2xl font-bold mb-2">{t.reset.title}</h1>
                <p className="text-gray-400">{t.reset.subtitle}</p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-6">
                <div>
                  <Label>{t.reset.password}</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 bg-white/5 border-white/10"
                      data-testid="new-password-input"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t.reset.requirements}</p>
                </div>

                <div>
                  <Label>{t.reset.confirmPassword}</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 bg-white/5 border-white/10"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                  data-testid="reset-password-btn"
                >
                  {loading ? '...' : t.reset.submit}
                </Button>
              </form>
            </div>
          )}

          {/* Success */}
          {step === 'success' && (
            <div className="p-8 bg-white/5 rounded-2xl border border-white/10 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold mb-2">{t.success.title}</h1>
              <p className="text-gray-400 mb-6">{t.success.subtitle}</p>
              
              <Button
                onClick={() => navigate('/login')}
                className="bg-[#D4AF37] hover:bg-[#B8963A] text-black"
              >
                {t.success.login}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
